import { Navigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Row } from 'react-bootstrap';
import { MFAFlow, MFAType } from './MFA.types';
import useMFASubmit from './useMFASubmit';
import { FormContainer, Title } from './MFA.styles';
import MFASchema from './MFA.schema';
import { HelperTextMuted } from '@/components/mfaForm/MFACodeForm.styles';
import MFACodeForm from '@/components/mfaForm/MFACodeForm';
import { Form, FormProvider } from '@/components/forms/form/Form';

interface MFAFormProps {
  mfaType: MFAType;
  mfaCode: string;
  mfaCodeSentTo: string;
  mfaFlow: MFAFlow;
}

const MFAForm = ({
  mfaType,
  mfaCode,
  mfaCodeSentTo,
  mfaFlow,
}: MFAFormProps) => {
  const { t } = useTranslation();
  const {
    handleSubmitMFA,
    handleResendCode,
    isSubmitting,
    isResending,
    resendCooldownSeconds,
  } = useMFASubmit({ mfaCode });

  return (
    <Form onSubmit={handleSubmitMFA}>
      <Row className="justify-content-center">
        <FormContainer>
          <Title>{t('pageLogin.titleMFA', { context: mfaFlow })}</Title>
          <MFACodeForm
            helperText={
              <HelperTextMuted>
                {t('pageLogin.pleaseEnterCode', { context: mfaType })}{' '}
                <span>{mfaCodeSentTo}</span>
              </HelperTextMuted>
            }
            isSubmitting={isSubmitting}
            isResending={isResending}
            handleResendCode={handleResendCode}
            resendCooldownSeconds={resendCooldownSeconds}
          />
        </FormContainer>
      </Row>
    </Form>
  );
};

const MFAPage = () => {
  const [searchParams] = useSearchParams();
  const mfaCode = searchParams.get('mfaCode') ?? '';
  const mfaType = (searchParams.get('mfaType') as MFAType) ?? MFAType.EMAIL;
  const mfaFlow = (searchParams.get('mfaFlow') as MFAFlow) ?? MFAFlow.LOGIN;
  const mfaCodeSentTo = searchParams.get('mfaCodeSentTo') ?? '';

  if (!mfaCode) {
    return <Navigate to="/login" replace />;
  }

  return (
    <FormProvider resolver={MFASchema()} defaultValues={{ pinCode: '' }}>
      <MFAForm
        mfaType={mfaType}
        mfaCode={mfaCode}
        mfaCodeSentTo={mfaCodeSentTo}
        mfaFlow={mfaFlow}
      />
    </FormProvider>
  );
};

export default MFAPage;
